// 유튜브 댓글 여론 분석 (2026-10-12)
// 영상 주소 → 서버 GET /yt-comments (인기순 댓글을 모아 Gemini 로 긍정·부정·중립 + 비꼼 판정)
// 분석은 30초~1분 걸려서 3초마다 같은 주소로 다시 물어보며 진행 상황을 보여 줌
import { useState, useEffect, useRef, useMemo } from "react";
import { mono, BASE_URL } from "./utils";
import { Ico } from "./lib/icons";

const card = { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 14, padding: 20, marginBottom: 14 };
const POS = "#10b981", NEG = "#ef4444", SAR = "#f59e0b", NEU = "#94a3b8";
const LABEL = { pos: "긍정", neg: "부정", neu: "중립" };
const COLOR = { pos: POS, neg: NEG, neu: NEU };
const VERDICT_COLOR = { "대체로 긍정적": POS, "긍정 우세": POS, "엇갈림": SAR, "부정 우세": NEG, "대체로 부정적": NEG, "의견이 적음": NEU };
const fmtN = (n) => n == null ? "—" : n >= 1e8 ? `${(n / 1e8).toFixed(1)}억` : n >= 1e4 ? `${(n / 1e4).toFixed(n >= 1e5 ? 0 : 1)}만` : n.toLocaleString("ko-KR");
const RECENT_KEY = "dgds-yt-recent";
const loadRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; } };
const saveRecent = (list) => { try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 8))); } catch { /* 저장 못 해도 화면은 동작 */ } };

// 영상 ID (서버와 같은 규칙)
const videoIdOf = (input) => {
  const t = String(input || "").trim();
  if (/^[\w-]{11}$/.test(t)) return t;
  const m = t.match(/(?:v=|youtu\.be\/|shorts\/|live\/|embed\/)([\w-]{11})/);
  return m ? m[1] : null;
};
// 댓글 속 '12:40', '1:02:03' 같은 시간 → 초
const TS_RE = /\b(?:(\d{1,2}):)?(\d{1,2}):(\d{2})\b/g;
const toSec = (h, m, s) => (+(h || 0)) * 3600 + (+m) * 60 + (+s);

// 영상 플레이어 — youtube-nocookie(쿠키를 덜 남기는 유튜브 공식 주소). enablejsapi 로 댓글 시간 클릭 시 그 장면으로 이동
function Player({ id, playerRef }) {
  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}><div style={{ position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden", background: "#000" }}>
      <iframe ref={playerRef} key={id} title="유튜브 영상" src={`https://www.youtube-nocookie.com/embed/${id}?enablejsapi=1&rel=0&playsinline=1`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen referrerPolicy="strict-origin-when-cross-origin"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
    </div></div>
  );
}

// 댓글 글자 안의 시간 표시를 눌러서 영상 이동
function CommentText({ text, onSeek }) {
  const out = []; let last = 0, m;
  TS_RE.lastIndex = 0;
  while ((m = TS_RE.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const sec = toSec(m[1], m[2], m[3]);
    out.push(onSeek ? <button key={m.index} onClick={() => onSeek(sec)} title="이 장면으로 이동" style={{ border: "none", background: "transparent", padding: 0, color: "var(--link)", cursor: "pointer", font: "inherit", textDecoration: "underline" }}>{m[0]}</button> : m[0]);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return <>{out}</>;
}

const Title = ({ icon, children, right }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center" }}>{icon && <Ico n={icon} />}{children}</div>
    {right}
  </div>
);

// 가로 막대 (긍정 | 중립 | 부정(진심) | 부정(비꼼))
function Bar({ parts }) {
  const total = parts.reduce((a, p) => a + p.v, 0) || 1;
  return (
    <div style={{ display: "flex", height: 26, borderRadius: 8, overflow: "hidden", background: "var(--grid-line)" }}>
      {parts.filter(p => p.v > 0).map(p => (
        <div key={p.k} title={`${p.k} ${(p.v / total * 100).toFixed(1)}%`} style={{ width: `${p.v / total * 100}%`, background: p.c, color: "#fff", fontSize: 11.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", whiteSpace: "nowrap", ...(p.stripe ? { backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,.18) 0 6px, transparent 6px 12px)" } : {}) }}>
          {p.v / total >= 0.08 ? `${p.k} ${Math.round(p.v / total * 100)}%` : ""}
        </div>
      ))}
    </div>
  );
}

function Progress({ st }) {
  const pct = st.total ? Math.round(st.done / st.total * 100) : 0;
  return (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{st.step || "분석 중"}… <span style={{ ...mono, fontSize: 12, color: "var(--text-muted)", fontWeight: 400 }}>{st.elapsed ? `${st.elapsed}초` : ""}</span></div>
      <div style={{ height: 8, borderRadius: 6, background: "var(--grid-line)", marginTop: 10, overflow: "hidden" }}>
        <div style={{ width: `${st.total ? Math.max(6, pct) : 6}%`, height: "100%", background: "var(--primary)", transition: "width .6s" }} />
      </div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8 }}>댓글 300개 기준 30초~1분 정도 걸립니다. 이 화면을 떠나도 분석은 계속되고, 같은 영상을 다시 넣으면 결과가 바로 나옵니다.</div>
    </div>
  );
}

function VideoHead({ v, small, count, noThumb }) {
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
      {!noThumb && <a href={v.url} target="_blank" rel="noopener noreferrer"><img src={v.thumbnail} alt="" width={small ? 128 : 192} height={small ? 72 : 108} style={{ borderRadius: 10, objectFit: "cover", display: "block", background: "var(--grid-line)" }} /></a>}
      <div style={{ flex: 1, minWidth: 220 }}>
        <a href={v.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: small ? 14 : 16, fontWeight: 700, color: "var(--text)", textDecoration: "none", lineHeight: 1.45, wordBreak: "keep-all" }}>{v.title}</a>
        <div style={{ fontSize: 12.5, color: "var(--text-sub)", marginTop: 4 }}>{v.channel}{v.publishedAt ? ` · ${v.publishedAt.slice(0, 10)}` : ""}</div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 6, fontSize: 12.5, color: "var(--text-muted)" }}>
          <span>조회수 <b style={{ ...mono, color: "var(--text)" }}>{fmtN(v.views)}</b></span>
          <span>좋아요 <b style={{ ...mono, color: "var(--text)" }}>{fmtN(v.likes)}</b></span>
          <span>댓글 <b style={{ ...mono, color: "var(--text)" }}>{fmtN(v.comments)}</b></span>
          {count != null && <span>분석한 댓글 <b style={{ ...mono, color: "var(--primary)" }}>{count}개</b> (인기순)</span>}
        </div>
      </div>
    </div>
  );
}

function CommentList({ comments, onSeek }) {
  const [f, setF] = useState("neg");
  const [n, setN] = useState(15);
  const counts = { all: comments.length, pos: comments.filter(c => c.s === "pos").length, neg: comments.filter(c => c.s === "neg" && !c.sarcasm).length, sarcasm: comments.filter(c => c.sarcasm).length, neu: comments.filter(c => c.s === "neu").length };
  const list = useMemo(() => comments.filter(c => f === "all" ? true : f === "sarcasm" ? c.sarcasm : f === "neg" ? c.s === "neg" && !c.sarcasm : c.s === f).sort((a, b) => b.likes - a.likes), [comments, f]);
  const tabs = [["neg", "부정", NEG], ["sarcasm", "비꼼", SAR], ["pos", "긍정", POS], ["neu", "중립", NEU], ["all", "전체", "var(--text-sub)"]];
  return (
    <div style={card}>
      <Title icon="messages-square">댓글 보기 <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 400, marginLeft: 6 }}>좋아요 많은 순</span></Title>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {tabs.map(([k, t, c]) => (
          <button key={k} onClick={() => { setF(k); setN(15); }} style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", fontWeight: f === k ? 700 : 500, border: "1px solid " + (f === k ? c : "var(--border)"), background: f === k ? c + "22" : "transparent", color: f === k ? c : "var(--text-sub)" }}>{t} <span style={mono}>{counts[k]}</span></button>
        ))}
      </div>
      {list.length === 0 && <div style={{ fontSize: 13, color: "var(--text-muted)", padding: "10px 0" }}>해당하는 댓글이 없습니다.</div>}
      {list.slice(0, n).map((c, i) => {
        const col = c.sarcasm ? SAR : COLOR[c.s];
        return (
          <div key={i} style={{ padding: "11px 0", borderTop: i ? "1px solid var(--border-light, var(--grid-line))" : "none", display: "flex", gap: 12 }}>
            <div style={{ flexShrink: 0, width: 46, textAlign: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: col, border: `1px solid ${col}`, borderRadius: 6, padding: "1px 0" }}>{c.sarcasm ? "비꼼" : LABEL[c.s]}</div>
              <div style={{ ...mono, fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>♥ {fmtN(c.likes)}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, color: "var(--text)", lineHeight: 1.65, wordBreak: "break-word" }}><CommentText text={c.text} onSeek={onSeek} /></div>
              {(c.why || c.topic || c.replies > 0) && (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4, fontSize: 11.5, color: "var(--text-muted)" }}>
                  {c.why && <span style={{ color: SAR }}>판정 이유: {c.why}</span>}
                  {c.topic && <span># {c.topic}</span>}
                  {c.replies > 0 && <span>답글 {c.replies}</span>}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {list.length > n && <button onClick={() => setN(n + 20)} style={{ marginTop: 8, width: "100%", padding: "9px 0", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--text-sub)", cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>더 보기 ({list.length - n}개 남음)</button>}
    </div>
  );
}

function Result({ r, onSeek }) {
  const s = r.stats;
  const vc = VERDICT_COLOR[s.verdict] || NEU;
  const negPlain = s.neg - s.sarcasm;
  return (
    <>
      <div style={card}>
        <Title icon="chart-column" right={<span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{r.analyzedAt ? new Date(new Date(r.analyzedAt).getTime() + 9 * 3600 * 1000).toISOString().slice(5, 16).replace("T", " ").replace("-", "/") + " 분석" : ""}</span>}>댓글 여론</Title>
        <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: vc }}>{s.verdict}</div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "var(--text-muted)", marginBottom: 4 }}><span>부정</span><span>여론 점수 <b style={{ ...mono, color: vc, fontSize: 13 }}>{s.score}</b> / 100</span><span>긍정</span></div>
            <div style={{ position: "relative", height: 10, borderRadius: 6, background: `linear-gradient(90deg, ${NEG}, ${SAR} 50%, ${POS})` }}>
              <div style={{ position: "absolute", left: `calc(${s.score}% - 7px)`, top: -4, width: 14, height: 18, borderRadius: 4, background: "var(--bg-card)", border: `3px solid ${vc}` }} />
            </div>
          </div>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-sub)", marginBottom: 6 }}>댓글 개수 기준</div>
        <Bar parts={[{ k: "긍정", v: s.pos, c: POS }, { k: "중립", v: s.neu, c: NEU }, { k: "부정", v: negPlain, c: NEG }, { k: "비꼼", v: s.sarcasm, c: SAR, stripe: true }]} />
        <div style={{ fontSize: 12.5, color: "var(--text-sub)", margin: "12px 0 6px" }}>좋아요 반영 (공감을 많이 받은 댓글에 무게)</div>
        <Bar parts={[{ k: "긍정", v: s.wPos, c: POS }, { k: "중립", v: s.wNeu, c: NEU }, { k: "부정", v: s.wNeg, c: NEG }]} />
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 12, fontSize: 12.5, color: "var(--text-sub)" }}>
          <span><b style={{ color: POS }}>긍정</b> {s.pos}개 ({s.pPos}%)</span>
          <span><b style={{ color: NEG }}>부정</b> {s.neg}개 ({s.pNeg}%)</span>
          <span><b style={{ color: SAR }}>그중 비꼼</b> {s.sarcasm}개</span>
          <span><b style={{ color: NEU }}>중립</b> {s.neu}개 ({s.pNeu}%)</span>
        </div>
        {s.sarcasm > 0 && (
          <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 10, background: SAR + "1a", border: `1px solid ${SAR}55`, fontSize: 13, color: "var(--text)", lineHeight: 1.65 }}>
            <b style={{ color: SAR }}>비꼬는 댓글 {s.sarcasm}개 ({s.pSarcasm}%)</b> — 겉으로는 칭찬·동의처럼 보이지만 속뜻이 조롱·비판인 댓글입니다. 속뜻 기준으로 부정에 넣었습니다.
          </div>
        )}
        {r.failed > 0 && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 10 }}>※ 댓글 {r.failed}개는 AI 판정에 실패해 중립으로 셌습니다.</div>}
      </div>

      {(r.summary?.length > 0 || r.praise?.length > 0 || r.criticism?.length > 0) && (
        <div style={card}>
          <Title icon="lightbulb">요약</Title>
          {r.summary.map((t, i) => <p key={i} style={{ fontSize: 13.5, color: "var(--text)", lineHeight: 1.8, margin: "0 0 8px", wordBreak: "keep-all" }}>{t}</p>)}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginTop: 8 }}>
            {[["좋아하는 점", r.praise, POS], ["비판·비꼬는 점", r.criticism, NEG]].map(([t, list, c]) => list?.length > 0 && (
              <div key={t}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: c, marginBottom: 6 }}>{t}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{list.map(x => <span key={x} style={{ fontSize: 12.5, padding: "4px 10px", borderRadius: 7, background: c + "1a", color: c, border: `1px solid ${c}44` }}>{x}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {s.topics?.length > 0 && (
        <div style={card}>
          <Title icon="grid-3x3">많이 나온 논점</Title>
          {s.topics.map(t => {
            const max = s.topics[0].n || 1;
            return (
              <div key={t.topic} style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 0", fontSize: 13 }}>
                <span style={{ width: 110, flexShrink: 0, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.topic}</span>
                <div style={{ flex: 1, display: "flex", height: 12, borderRadius: 4, overflow: "hidden", background: "var(--grid-line)", maxWidth: `${Math.max(12, t.n / max * 100)}%` }}>
                  <div style={{ width: `${t.pos / t.n * 100}%`, background: POS }} />
                  <div style={{ width: `${(t.n - t.pos - t.neg) / t.n * 100}%`, background: NEU }} />
                  <div style={{ width: `${t.neg / t.n * 100}%`, background: NEG }} />
                </div>
                <span style={{ ...mono, fontSize: 12, color: "var(--text-muted)", width: 90, textAlign: "right", flexShrink: 0 }}>{t.n}개 <span style={{ color: POS }}>{t.pos}</span>/<span style={{ color: NEG }}>{t.neg}</span></span>
              </div>
            );
          })}
        </div>
      )}

      <CommentList comments={r.comments || []} onSeek={onSeek} />
    </>
  );
}

export default function YoutubeComments() {
  const [url, setUrl] = useState("");
  const [max, setMax] = useState(300);
  const [st, setSt] = useState(null);
  const [recent, setRecent] = useState(loadRecent);
  const timer = useRef(null);
  const alive = useRef(true);
  const playerRef = useRef(null);
  const boxRef = useRef(null);
  const [vid, setVid] = useState(null); // 재생할 영상 (분석 버튼을 누르면 바로 표시)
  const seek = (sec) => {
    const w = playerRef.current?.contentWindow;
    if (!w) return;
    const send = (func, args) => w.postMessage(JSON.stringify({ event: "command", func, args }), "*");
    send("seekTo", [sec, true]); send("playVideo", []);
    boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  useEffect(() => () => { alive.current = false; clearTimeout(timer.current); }, []);

  const run = (u = url, m = max) => {
    if (!u.trim()) return;
    clearTimeout(timer.current);
    setVid(videoIdOf(u));
    setSt({ status: "running", step: "요청 보내는 중" });
    const poll = () => {
      fetch(`${BASE_URL}/yt-comments?url=${encodeURIComponent(u.trim())}&max=${m}`)
        .then(r => r.json().then(j => ({ ok: r.ok, j })))
        .then(({ ok, j }) => {
          if (!alive.current) return;
          if (!ok || j.error) { setSt({ status: "error", error: j.error || "분석에 실패했습니다." }); return; }
          setSt(j);
          if (j.status === "running") timer.current = setTimeout(poll, 3000);
          else if (j.status === "done") {
            const item = { url: j.video.url, title: j.video.title, verdict: j.stats.verdict, max: m };
            setRecent(prev => { const next = [item, ...prev.filter(x => x.url !== item.url)].slice(0, 8); saveRecent(next); return next; });
          }
        })
        .catch(() => { if (alive.current) setSt({ status: "error", error: "서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요." }); });
    };
    poll();
  };

  const busy = st?.status === "running";
  return (
    <div style={{ maxWidth: 1080, margin: "0 auto" }}>
      <div style={card}>
        <Title icon="square-play">유튜브 댓글 여론 분석</Title>
        <div style={{ fontSize: 13, color: "var(--text-sub)", lineHeight: 1.7, marginBottom: 12, wordBreak: "keep-all" }}>영상 주소를 넣으면 인기 댓글을 모아 AI가 하나씩 <b style={{ color: POS }}>긍정</b>·<b style={{ color: NEG }}>부정</b>·<b style={{ color: NEU }}>중립</b>으로 나누고, 겉으로는 칭찬인데 속뜻은 조롱인 <b style={{ color: SAR }}>비꼬는 댓글</b>도 따로 잡아 부정으로 셉니다.</div>
        <form onSubmit={(e) => { e.preventDefault(); run(); }} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=… 또는 youtu.be/… · shorts 주소도 됩니다" style={{ flex: 1, minWidth: 260, padding: "10px 12px", borderRadius: 9, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text)", fontSize: 14, fontFamily: "inherit" }} />
          <select value={max} onChange={e => setMax(+e.target.value)} style={{ padding: "10px 10px", borderRadius: 9, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text)", fontSize: 13.5, fontFamily: "inherit" }}>
            <option value={100}>댓글 100개</option><option value={300}>댓글 300개</option><option value={500}>댓글 500개</option>
          </select>
          <button type="submit" disabled={busy || !url.trim()} style={{ padding: "10px 20px", borderRadius: 9, border: "none", background: busy || !url.trim() ? "var(--grid-line)" : "var(--primary)", color: "#fff", fontWeight: 700, fontSize: 14, cursor: busy ? "default" : "pointer", fontFamily: "inherit" }}>{busy ? "분석 중…" : "분석"}</button>
        </form>
        {recent.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>최근 분석</span>
            {recent.map(x => (
              <button key={x.url} onClick={() => { setUrl(x.url); setMax(x.max || 300); run(x.url, x.max || 300); }} title={x.title} style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "4px 10px", borderRadius: 999, border: "1px solid var(--border)", background: "transparent", color: "var(--text-sub)", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                <span style={{ color: VERDICT_COLOR[x.verdict] || NEU, fontWeight: 700 }}>{x.verdict}</span> · {x.title}
              </button>
            ))}
          </div>
        )}
      </div>
      {vid && (
        <div ref={boxRef} style={card}>
          <Player id={vid} playerRef={playerRef} />
          {st?.video && <div style={{ marginTop: 12 }}><VideoHead v={st.video} noThumb count={st.status === "done" ? st.stats?.total : null} /></div>}
        </div>
      )}
      {st?.status === "running" && <Progress st={st} />}
      {st?.status === "error" && <div style={{ ...card, color: NEG, fontSize: 13.5 }}>{st.error}</div>}
      {st?.status === "done" && <Result r={st} onSeek={seek} />}
      <div style={{ fontSize: 11.5, color: "var(--text-muted)", padding: "4px 4px 20px", lineHeight: 1.7 }}>· 인기순 상위 댓글(답글 제외)만 분석합니다. AI 판정이라 일부 틀릴 수 있으며, 특히 짧은 댓글의 비꼼은 문맥에 따라 다르게 읽힐 수 있습니다. · 같은 영상·같은 개수는 1시간 동안 다시 분석하지 않고 저장된 결과를 보여 줍니다. · 댓글 속 시간(예: 12:40)을 누르면 위 영상이 그 장면으로 이동합니다.</div>
    </div>
  );
}
